import './loader-global.css';
import cx from 'classnames';
import React, { ReactElement, useEffect, useState } from 'react';
import { parseDOMString, removeElement } from 'packages/radiant-react/utils';
import { SafeHTML } from 'packages/radiant-react/safe-html';
import { getLoaderTemplate } from './loader-global-template';

export interface LoaderGlobalProps {
  /**
   * children for the loader
   */
  children: ReactElement | ReactElement[];
  /**
   * show / hide the loader
   * @default false
   */
  hideLoader?: boolean;
  /**
   * unique id for the loader
   * @default ''
   */
  id?: string;
  /**
   * class name for the loader
   */
  className?: string;
  /**
   * text shown while loading
   * @default ''
   */
  loadingText?: string;

  /**
   * array of text shown after wait time
   * @default ''
   */
  inProgressLoadingMessages?: string[];

  /**
   * time after which the loading text changes
   * @default ''
   */
  loadingWaitTime?: number;
  /**
   * set background transparent for loader
   * @default false
   */
  isTransparentBg?: boolean;
  /**
   *
   * Opacity level for the loading indicator can be controlled by this flag
   * @default false
   */
  highOpacity?: boolean;
  /**
   * To style the text displaying loading status
   */
  loadingTextClassName?: string;
  /**
   * Overlay loading
   */
  isLoaderInOverlay?: boolean;
}

export const LoaderGlobal: React.FC<LoaderGlobalProps> = ({
  children,
  id,
  className,
  loadingText = '',
  inProgressLoadingMessages = [],
  loadingWaitTime = 30000,
  hideLoader = false,
  isTransparentBg = false,
  highOpacity = false,
  loadingTextClassName = '',
  isLoaderInOverlay = false,
  ...restProps
}) => {
  const [loadingInfoText, setLoadingInfoText] = useState(loadingText);

  useEffect(() => {
    let count = 0;
    // If loading text is '' then take the first value from inProgressLoadingMessages
    if (loadingText === '' && inProgressLoadingMessages.length > 0) {
      setLoadingInfoText(inProgressLoadingMessages[0]);
      count = 1;
    } else {
      setLoadingInfoText(loadingText);
    }
    const timer = setInterval(() => {
      if (count < inProgressLoadingMessages?.length) {
        setLoadingInfoText(inProgressLoadingMessages[count]);
        count++;
      } else {
        clearInterval(timer);
      }
    }, loadingWaitTime);
    return () => clearTimeout(timer);
  }, [inProgressLoadingMessages, loadingText, loadingWaitTime]);

  // Reference for the class is in loader-global.scss
  let opacityClass = highOpacity ? 'rdLoadingIndicatorHighOpacity' : 'rdLoadingIndicatorLowOpacity';

  if (isLoaderInOverlay) {
    opacityClass = 'rdLoadingIndicatorOverlay';
  }

  const loaderClassNames = cx(
    'rdLoaderGlobalWrap',
    {
      rdHideLoader: hideLoader,
    },
    className
  );
  const template = getLoaderTemplate(opacityClass, loadingTextClassName);

  const getloaderHtmlString = (text: string) => {
    if (text) {
      return parseDOMString(template, '#data-loading-text', text);
    }
    return removeElement(template, '#data-loading-text');
  };

  return (
    <div
      id={id}
      className={loaderClassNames}
      data-display-name={LoaderGlobal.displayName}
      {...restProps}
    >
      <SafeHTML
        html={getloaderHtmlString(loadingInfoText)}
        className={isTransparentBg ? 'rdOverrideStyle' : ''}
      />
      {children}
    </div>
  );
};

LoaderGlobal.displayName = 'radiant_react_loading_indicator_global';
